import React, { useState, useEffect, useCallback } from 'react';

// ── Mirrors the Lg theme from App.js exactly ──────────────────────
const Lg = {
  bg:'#f0f4f8', panel:'#ffffff', panel2:'#f8fafc',
  border:'#e2e8f0', border2:'#cbd5e1',
  text:'#1e293b', muted:'#64748b', dim:'#94a3b8',
  amber:'#d97706', amberBg:'#fffbeb', amberBrd:'#fde68a',
  green:'#16a34a', greenBg:'#f0fdf4', greenBrd:'#bbf7d0',
  red:'#dc2626',   redBg:'#fef2f2',   redBrd:'#fecaca',
  teal:'#0369a1',  tealBg:'#eff6ff',  tealBrd:'#bfdbfe',
  purple:'#7c3aed',purpleBg:'#f5f3ff',purpleBrd:'#ddd6fe',
};

// ── Micro-components matching app style ───────────────────────────
const Card = ({ style, children }) => (
  <div style={{
    background: Lg.panel, borderRadius: 12,
    border: `1px solid ${Lg.border}`,
    overflow: 'hidden', ...style,
  }}>
    {children}
  </div>
);

const CardHeader = ({ title, right }) => (
  <div style={{
    padding: '10px 14px',
    borderBottom: `1px solid ${Lg.border}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: Lg.muted, letterSpacing: 1 }}>
      {title}
    </div>
    {right}
  </div>
);

const Badge = ({ label, c, bg, bd }) => (
  <span style={{
    fontSize: 9, fontWeight: 700, color: c, background: bg,
    border: `1px solid ${bd}`, padding: '2px 7px', borderRadius: 10,
  }}>
    {label}
  </span>
);

const MetricCard = ({ label, value, color }) => (
  <div style={{
    background: Lg.panel2, borderRadius: 9,
    padding: '11px 12px', border: `1px solid ${Lg.border}`,
  }}>
    <div style={{ fontSize: 8, color: Lg.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 3 }}>
      {label}
    </div>
    <div style={{ fontSize: 16, fontWeight: 800, color: color || Lg.text }}>
      {value}
    </div>
  </div>
);

// ── Win rate bar row ───────────────────────────────────────────────
const WinBar = ({ label, pct, count, isMobile }) => {
  const c = pct >= 65 ? Lg.green : pct >= 50 ? Lg.amber : Lg.red;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: Lg.muted, fontWeight: 600,
        minWidth: isMobile ? 60 : 72, textAlign: 'right' }}>
        {label}
      </div>
      <div style={{ flex: 1, background: Lg.panel2, borderRadius: 99,
        height: 12, overflow: 'hidden', border: `1px solid ${Lg.border}` }}>
        <div style={{
          width: `${Math.min(pct, 100)}%`, height: '100%',
          background: c, borderRadius: 99,
          transition: 'width 0.5s ease',
        }} />
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: c, minWidth: 32 }}>
        {count > 0 ? `${pct.toFixed(0)}%` : '—'}
      </div>
      <div style={{ fontSize: 9, color: Lg.dim, minWidth: 24 }}>
        {count > 0 ? `${count}t` : ''}
      </div>
    </div>
  );
};

// ── Empty / loading states ────────────────────────────────────────
const EmptyState = ({ msg }) => (
  <div style={{ textAlign: 'center', padding: '32px 16px',
    color: Lg.muted, fontSize: 12, lineHeight: 1.8 }}>
    <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
    {msg}
  </div>
);

// ── Main component ────────────────────────────────────────────────
export default function AnalyticsTab({ API_URL, isMobile }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const fetch_data = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/signals/accuracy`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setData(json);
      setLastFetch(new Date());
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [API_URL]);

  useEffect(() => { fetch_data(); }, [fetch_data]);

  const fmt = (n) => n == null ? '—' :
    Math.round(n).toLocaleString('en-IN');
  const pnlStr = (v) => `${v >= 0 ? '+' : ''}₹${fmt(v)}`;

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div style={{ paddingBottom: 80 }}>

      {/* ── Header card ─────────────────────────────────────── */}
      <Card style={{ marginBottom: 10 }}>
        <CardHeader
          title="SIGNAL ACCURACY"
          right={
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {data && !loading && (
                <span style={{ fontSize: 8, color: Lg.dim }}>
                  {lastFetch ? lastFetch.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              )}
              <button
                className="btn"
                onClick={fetch_data}
                disabled={loading}
                style={{
                  fontSize: 9, fontWeight: 700, color: Lg.amber,
                  background: Lg.amberBg, border: `1px solid ${Lg.amberBrd}`,
                  padding: '3px 8px', borderRadius: 8,
                  opacity: loading ? 0.5 : 1,
                }}
              >
                {loading ? '...' : '↻ Refresh'}
              </button>
            </div>
          }
        />
        <div style={{ padding: 14 }}>
          {loading && !data && <EmptyState msg="Loading accuracy data…" />}
          {error && !data && (
            <EmptyState msg={`Could not load: ${error}`} />
          )}
          {data && data.total_resolved === 0 && (
            <EmptyState msg={
              "No resolved signal outcomes yet.\n" +
              "Execute trades from signal cards, let them hit SL or Target,\n" +
              "then come back here."
            } />
          )}
          {data && data.total_resolved > 0 && (() => {
            const o = data.overall;
            return (
              <>
                {/* Metric cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                  gap: 8, marginBottom: 14,
                }}>
                  <MetricCard
                    label="SIGNALS TRACKED"
                    value={o.total}
                    color={Lg.teal}
                  />
                  <MetricCard
                    label="WIN RATE"
                    value={`${o.win_rate.toFixed(1)}%`}
                    color={o.win_rate >= 60 ? Lg.green : o.win_rate < 50 ? Lg.red : Lg.amber}
                  />
                  <MetricCard
                    label="PROFIT FACTOR"
                    value={`${o.profit_factor}×`}
                    color={o.profit_factor >= 1.5 ? Lg.green : o.profit_factor < 1 ? Lg.red : Lg.amber}
                  />
                  <MetricCard
                    label="AVG P&L / TRADE"
                    value={pnlStr(o.avg_pnl)}
                    color={o.avg_pnl >= 0 ? Lg.green : Lg.red}
                  />
                </div>

                {/* Recent trend pill */}
                {data.recent_trend && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    marginBottom: 14,
                    background: Lg.panel2, borderRadius: 8,
                    padding: '7px 10px', border: `1px solid ${Lg.border}`,
                  }}>
                    <span style={{ fontSize: 9, color: Lg.muted, fontWeight: 700 }}>
                      RECENT TREND
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 800,
                      color: data.recent_trend.direction === 'improving' ? Lg.green : Lg.red,
                    }}>
                      {data.recent_trend.direction === 'improving' ? '↑' : '↓'}{' '}
                      {data.recent_trend.latest_10_win_rate}%
                    </span>
                    <span style={{ fontSize: 9, color: Lg.muted }}>
                      win rate (last 10) vs {data.recent_trend.prev_10_win_rate}% previously
                    </span>
                  </div>
                )}

                {/* Total P&L banner */}
                <div style={{
                  background: o.total_pnl >= 0 ? Lg.greenBg : Lg.redBg,
                  border: `1px solid ${o.total_pnl >= 0 ? Lg.greenBrd : Lg.redBrd}`,
                  borderRadius: 9, padding: '9px 12px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 2,
                }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: Lg.muted, letterSpacing: 1 }}>
                    TOTAL REALISED P&L
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 800,
                    color: o.total_pnl >= 0 ? Lg.green : Lg.red }}>
                    {pnlStr(o.total_pnl)}
                  </span>
                </div>
              </>
            );
          })()}
        </div>
      </Card>

      {/* ── Win rate by confidence ──────────────────────────── */}
      {data && data.total_resolved > 0 && (
        <Card style={{ marginBottom: 10 }}>
          <CardHeader
            title="WIN RATE BY CONFIDENCE"
            right={
              <Badge label="AI calibration" c={Lg.purple} bg={Lg.purpleBg} bd={Lg.purpleBrd} />
            }
          />
          <div style={{ padding: 14 }}>
            <div style={{ fontSize: 9, color: Lg.dim, marginBottom: 12 }}>
              Does higher confidence actually mean more wins? It should.
            </div>
            {(data.by_confidence || []).map(b => (
              <WinBar
                key={b.bucket}
                label={b.bucket}
                pct={b.win_rate}
                count={b.total}
                isMobile={isMobile}
              />
            ))}
          </div>
        </Card>
      )}

      {/* ── Win rate by timeframe ───────────────────────────── */}
      {data && data.total_resolved > 0 && (
        <Card style={{ marginBottom: 10 }}>
          <CardHeader
            title="WIN RATE BY TIMEFRAME"
            right={
              <Badge label="best hold period" c={Lg.teal} bg={Lg.tealBg} bd={Lg.tealBrd} />
            }
          />
          <div style={{ padding: 14 }}>
            {(data.by_timeframe || []).filter(t => t.total > 0).map(t => (
              <WinBar
                key={t.timeframe}
                label={t.label}
                pct={t.win_rate}
                count={t.total}
                isMobile={isMobile}
              />
            ))}
          </div>
        </Card>
      )}

      {/* ── By direction ────────────────────────────────────── */}
      {data && data.total_resolved > 0 && (
        <Card style={{ marginBottom: 10 }}>
          <CardHeader title="BUY vs SELL" />
          <div style={{ padding: 14, display: 'grid',
            gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {['BUY', 'SELL'].map(dir => {
              const d = (data.by_direction || {})[dir] || {};
              const c = dir === 'BUY' ? Lg.green : Lg.red;
              const bg = dir === 'BUY' ? Lg.greenBg : Lg.redBg;
              const bd = dir === 'BUY' ? Lg.greenBrd : Lg.redBrd;
              return (
                <div key={dir} style={{
                  background: bg, borderRadius: 9,
                  padding: '11px 12px', border: `1px solid ${bd}`,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: c, marginBottom: 8 }}>
                    {dir === 'BUY' ? '📈' : '📉'} {dir}
                  </div>
                  {[
                    { l: 'WIN RATE', v: d.total > 0 ? `${d.win_rate.toFixed(0)}%` : '—', c },
                    { l: 'TRADES',   v: d.total || 0,          c: Lg.text },
                    { l: 'AVG P&L',  v: d.total > 0 ? pnlStr(d.avg_pnl) : '—',
                      c: (d.avg_pnl || 0) >= 0 ? Lg.green : Lg.red },
                  ].map(row => (
                    <div key={row.l} style={{
                      display: 'flex', justifyContent: 'space-between',
                      marginBottom: 4,
                    }}>
                      <span style={{ fontSize: 8, color: Lg.muted, fontWeight: 700, letterSpacing: 0.5 }}>
                        {row.l}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 800, color: row.c }}>
                        {row.v}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── AI Insights ─────────────────────────────────────── */}
      {data && data.total_resolved > 0 && data.insights && data.insights.length > 0 && (
        <Card style={{ marginBottom: 10 }}>
          <CardHeader
            title="AI INSIGHTS"
            right={<Badge label={`${data.insights.length} observations`} c={Lg.purple} bg={Lg.purpleBg} bd={Lg.purpleBrd} />}
          />
          <div style={{ padding: '10px 14px' }}>
            {data.insights.map((insight, i) => (
              <div key={i} style={{
                background: Lg.panel2,
                border: `1px solid ${Lg.border}`,
                borderRadius: 8, padding: '9px 11px',
                marginBottom: i < data.insights.length - 1 ? 8 : 0,
                fontSize: isMobile ? 11 : 12,
                color: Lg.text, lineHeight: 1.6,
              }}>
                {insight}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── By symbol table ─────────────────────────────────── */}
      {data && data.total_resolved > 0 && data.by_symbol && data.by_symbol.length > 0 && (
        <Card>
          <CardHeader
            title="BY SYMBOL"
            right={<Badge label={`top ${data.by_symbol.length}`} c={Lg.teal} bg={Lg.tealBg} bd={Lg.tealBrd} />}
          />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse',
              fontSize: isMobile ? 10 : 11 }}>
              <thead>
                <tr style={{ background: Lg.panel2, borderBottom: `1px solid ${Lg.border}` }}>
                  {(isMobile
                    ? ['Symbol', 'Win%', 'P&L']
                    : ['Symbol', 'Trades', 'Win Rate', 'Avg P&L', 'Total P&L']
                  ).map(h => (
                    <th key={h} style={{
                      padding: '9px 10px', textAlign: 'left',
                      color: Lg.muted, fontWeight: 700, fontSize: 9,
                      whiteSpace: 'nowrap', letterSpacing: 0.5,
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.by_symbol.map((s, i) => {
                  const winColor = s.win_rate >= 65 ? Lg.green
                    : s.win_rate < 50 ? Lg.red : Lg.amber;
                  return (
                    <tr key={s.symbol} className="trow" style={{
                      borderBottom: i < data.by_symbol.length - 1
                        ? `1px solid ${Lg.border}` : 'none',
                    }}>
                      <td style={{ padding: '9px 10px', fontWeight: 700, color: Lg.text }}>
                        {s.symbol}
                      </td>
                      {!isMobile && (
                        <td style={{ padding: '9px 10px', color: Lg.muted }}>
                          {s.total}
                        </td>
                      )}
                      <td style={{ padding: '9px 10px' }}>
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: winColor,
                          background: s.win_rate >= 65 ? Lg.greenBg
                            : s.win_rate < 50 ? Lg.redBg : Lg.amberBg,
                          border: `1px solid ${s.win_rate >= 65 ? Lg.greenBrd
                            : s.win_rate < 50 ? Lg.redBrd : Lg.amberBrd}`,
                          padding: '2px 7px', borderRadius: 10,
                        }}>
                          {s.win_rate.toFixed(0)}%
                        </span>
                      </td>
                      <td style={{ padding: '9px 10px', fontWeight: 700,
                        color: (s.avg_pnl || 0) >= 0 ? Lg.green : Lg.red }}>
                        {pnlStr(s.avg_pnl)}
                      </td>
                      {!isMobile && (
                        <td style={{ padding: '9px 10px', fontWeight: 700,
                          color: (s.total_pnl || 0) >= 0 ? Lg.green : Lg.red }}>
                          {pnlStr(s.total_pnl)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

    </div>
  );
}
